import { useState, type FormEvent } from "react";
import { SENHA_MINIMA, type SessaoAtual, type UsuarioPublico } from "@noesis/shared";
import { Grifo } from "../componentes/Marca.js";
import { FalhaDaApi, pedir } from "../api.js";

/**
 * A troca obrigatória da primeira entrada.
 *
 * Conta criada pelo `criar-conta` nasce com `deve_redefinir_senha` ligado, e a
 * senha sorteada serve para entrar uma vez e morrer nisso. Enquanto a coluna
 * estiver ligada o servidor recusa toda rota autenticada com 403
 * `senha_a_redefinir` — inclusive as internas, alcançadas colando o endereço.
 *
 * **Sem saída lateral, de propósito.** Não há botão de voltar nem link para o
 * resto do app: enquanto a trava está ligada não existe para onde ir, e um
 * caminho de fuga só levaria a outra tela recusando 403. Sair da conta
 * continua possível pelo botão do fim, que é a única saída honesta.
 *
 * A validação é do servidor. O comprimento mínimo vem de `SENHA_MINIMA`, o
 * mesmo número que o schema Zod usa para recusar — não uma cópia local que
 * pode divergir dele. O que esta tela confere sozinha é só a confirmação, que
 * é pergunta de formulário e não regra de negócio: o servidor nem a recebe.
 */
export function RedefinirSenha({
  usuario,
  aoTrocar,
  aoSair,
}: {
  usuario: UsuarioPublico;
  aoTrocar: (sessao: SessaoAtual) => void;
  aoSair: () => void;
}) {
  const [senhaAtual, setSenhaAtual] = useState("");
  const [senhaNova, setSenhaNova] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const naoConfere = confirmacao !== "" && senhaNova !== confirmacao;
  const podeEnviar = senhaAtual !== "" && senhaNova !== "" && senhaNova === confirmacao && !enviando;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!podeEnviar) return;
    setEnviando(true);
    setErro(null);
    try {
      aoTrocar(await pedir("sessao.redefinirSenha", { corpo: { senhaAtual, senhaNova } }));
    } catch (falha) {
      /*
       * A mensagem é a do servidor, e quando ele aponta campos é o primeiro
       * deles que aparece. Reescrevê-la aqui criaria duas verdades sobre a
       * mesma regra, e a daqui envelheceria primeiro.
       */
      setErro(
        falha instanceof FalhaDaApi
          ? (falha.campos?.[0]?.mensagem ?? falha.message)
          : "Não foi possível trocar a senha.",
      );
      setEnviando(false);
    }
  }

  return (
    <div className="tela tela--entrada">
      <div className="tela__corpo tela__corpo--centrado">
        <header className="entrada__cabecalho">
          <p className="entrada__marca">
            <Grifo tamanho="hero" />
          </p>
          <h1 className="t-titulo">Escolha a sua senha.</h1>
          <p className="leitura--apoio">
            A conta <strong>{usuario.email}</strong> foi criada com uma senha provisória, e ela vale só para
            esta primeira entrada. Defina a sua para continuar.
          </p>
        </header>

        <form className="pilha" onSubmit={(e) => void enviar(e)} noValidate>
          <div className="campo">
            <label className="campo__rotulo" htmlFor="senha-atual">
              Senha provisória
            </label>
            <input
              id="senha-atual"
              className="campo__entrada"
              type="password"
              autoComplete="current-password"
              required
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
              aria-invalid={erro ? true : undefined}
            />
          </div>

          <div className="campo">
            <label className="campo__rotulo" htmlFor="senha-nova">
              Senha nova
            </label>
            <input
              id="senha-nova"
              className="campo__entrada"
              type="password"
              autoComplete="new-password"
              required
              value={senhaNova}
              onChange={(e) => setSenhaNova(e.target.value)}
              aria-describedby="dica-senha"
              aria-invalid={erro ? true : undefined}
            />
            {/* O número sai do schema compartilhado (invariante 4). */}
            <p className="etiqueta tinta-suave" id="dica-senha">
              Pelo menos {SENHA_MINIMA} caracteres.
            </p>
          </div>

          <div className="campo">
            <label className="campo__rotulo" htmlFor="senha-confirmacao">
              Repita a senha nova
            </label>
            <input
              id="senha-confirmacao"
              className="campo__entrada"
              type="password"
              autoComplete="new-password"
              required
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              aria-invalid={naoConfere ? true : undefined}
            />
          </div>

          {/* Anunciado por leitor de tela no momento em que aparece. */}
          <p className="campo__erro" role="alert" aria-live="polite">
            {naoConfere ? "As duas senhas precisam ser iguais." : (erro ?? "")}
          </p>

          <button className="botao" type="submit" disabled={!podeEnviar}>
            {enviando ? "Trocando…" : "Trocar a senha e entrar"}
          </button>
        </form>

        <button className="botao botao--texto" type="button" onClick={aoSair}>
          Sair desta conta
        </button>
      </div>
    </div>
  );
}
